import Phaser from 'phaser'
import { Player } from '../entities/Player'
import { Creature } from '../entities/Creature'
import { CombatSystem } from './CombatSystem'
import { SpellType, SPELLS, EVENTS, DamageEvent } from '../types/GameTypes'

export class SpellSystem {
  private scene: Phaser.Scene
  private player: Player
  private combatSystem: CombatSystem

  // Cooldown tracking
  private cooldowns: Map<SpellType, number> = new Map()

  constructor(scene: Phaser.Scene, player: Player, combatSystem: CombatSystem) {
    this.scene = scene
    this.player = player
    this.combatSystem = combatSystem

    // Initialize cooldowns
    this.cooldowns.set('attack', 0)
    this.cooldowns.set('heal', 0)
    this.cooldowns.set('fireBolt', 0)
  }

  /**
   * Check if a spell can be cast
   */
  public canCast(spellType: SpellType): boolean {
    if (this.player.isDead) return false

    const spell = SPELLS[spellType]
    const now = this.scene.time.now
    const lastCast = this.cooldowns.get(spellType) || 0

    // Check cooldown
    if (now - lastCast < spell.cooldown) {
      return false
    }

    // Check mana
    if (this.player.stats.currentMp < spell.manaCost) {
      return false
    }

    // For attack spells, try to auto-target if no target
    if (spellType === 'attack' || spellType === 'fireBolt') {
      let target = this.combatSystem.getTarget()

      // Auto-target closest if no target
      if (!target || !target.isAlive()) {
        target = this.combatSystem.targetClosest()
      }

      if (!target || !target.isAlive()) {
        return false
      }

      // Check range
      const distance = this.player.distanceTo(target.x, target.y)
      if (distance > spell.range) {
        return false
      }
    }

    return true
  }

  /**
   * Cast a spell
   */
  public cast(spellType: SpellType): boolean {
    // For attack spells, try to auto-target first
    if (spellType === 'attack' || spellType === 'fireBolt') {
      const target = this.combatSystem.getTarget()
      if (!target || !target.isAlive()) {
        this.combatSystem.targetClosest()
      }
    }

    if (!this.canCast(spellType)) {
      return false
    }

    const spell = SPELLS[spellType]

    // Consume mana
    if (spell.manaCost > 0) {
      this.player.stats.currentMp -= spell.manaCost
      this.scene.events.emit(EVENTS.STATS_CHANGED)
    }

    // Set cooldown
    this.cooldowns.set(spellType, this.scene.time.now)

    // Execute spell effect
    switch (spellType) {
      case 'attack':
        this.castAttack()
        break
      case 'heal':
        this.castHeal()
        break
      case 'fireBolt':
        this.castFireBolt()
        break
    }

    return true
  }

  /**
   * Manual attack spell
   */
  private castAttack(): void {
    const target = this.combatSystem.getTarget()
    if (!target) return

    this.combatSystem.performAttack(target)
  }

  /**
   * Heal spell - restore HP
   */
  private castHeal(): void {
    const spell = SPELLS.heal
    const healAmount = spell.healAmount || 25

    this.player.heal(healAmount)

    // Visual feedback - green flash
    this.player.setTint(0x00ff00)
    this.scene.time.delayedCall(200, () => {
      if (!this.player.isDead) {
        this.player.clearTint()
      }
    })

    // Show healing number
    this.scene.events.emit(EVENTS.DAMAGE_DEALT, {
      attacker: 'player',
      target: 'player',
      damage: -healAmount, // Negative for heal
      isCritical: false,
      targetX: this.player.x,
      targetY: this.player.y
    } as DamageEvent)
  }

  /**
   * Fire Bolt spell - ranged damage
   */
  private castFireBolt(): void {
    const target = this.combatSystem.getTarget() as Creature
    if (!target) return

    const spell = SPELLS.fireBolt
    const minDmg = spell.minDamage || 20
    const maxDmg = spell.maxDamage || 30

    // Calculate damage
    const baseDamage = Phaser.Math.Between(minDmg, maxDmg)
    const isCritical = Math.random() < 0.1 // 10% crit chance
    const finalDamage = isCritical ? Math.floor(baseDamage * 1.5) : baseDamage

    // Apply damage to creature (bypasses defense for spells)
    const actualDamage = Math.max(1, finalDamage - Math.floor(target.definition.defense / 2))
    target.takeDamage(actualDamage, isCritical)

    // Visual effect - orange/red tint on player during cast
    this.player.setTint(0xff6600)
    this.scene.time.delayedCall(150, () => {
      if (!this.player.isDead) {
        this.player.clearTint()
      }
    })

    // Create a simple projectile effect (line from player to target)
    this.createFireBoltEffect(target.x, target.y)
  }

  /**
   * Simple fire bolt visual effect
   */
  private createFireBoltEffect(targetX: number, targetY: number): void {
    const graphics = this.scene.add.graphics()
    graphics.setDepth(500)

    // Draw a line from player to target
    graphics.lineStyle(4, 0xff6600, 1)
    graphics.beginPath()
    graphics.moveTo(this.player.x, this.player.y)
    graphics.lineTo(targetX, targetY)
    graphics.strokePath()

    // Impact flash at target
    graphics.fillStyle(0xff4400, 0.8)
    graphics.fillCircle(targetX, targetY, 15)

    // Fade out and destroy
    this.scene.tweens.add({
      targets: graphics,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        graphics.destroy()
      }
    })
  }

  /**
   * Get remaining cooldown for a spell (0-1 percentage)
   */
  public getCooldownPercent(spellType: SpellType): number {
    const spell = SPELLS[spellType]
    const now = this.scene.time.now
    const lastCast = this.cooldowns.get(spellType) || 0
    const elapsed = now - lastCast

    if (elapsed >= spell.cooldown) {
      return 0
    }

    return 1 - (elapsed / spell.cooldown)
  }

  /**
   * Check if player has enough mana for a spell
   */
  public hasEnoughMana(spellType: SpellType): boolean {
    const spell = SPELLS[spellType]
    return this.player.stats.currentMp >= spell.manaCost
  }
}
